import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddCircleOutline as AddIcon,
  CreateNewFolder as CreateNewFolderIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CloseButton from './Common/CloseButton';
import { hasUserTagVariableReservedChars } from '../Utils/tagUtils';

const primaryColor = '#5757b0';
const hoverColor = '#7777d0';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#fff',
    '&:hover fieldset': { borderColor: hoverColor },
    '&.Mui-focused fieldset': { borderColor: primaryColor },
  },
  '& .MuiInputLabel-outlined.Mui-focused': { color: primaryColor },
};

const ManualCreationDialog = ({ open, onClose, onCreateProject }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState([]);
  const [errors, setErrors] = useState({});

  const steps = [
    t('addcase.manual_step_title', '1. Donner un titre'),
    t('addcase.manual_step_information', '2. Donner les informations'),
    t('addcase.manual_step_extraction', '3. Que voulez-vous extraire ?'),
  ];

  const reset = () => {
    setStep(0);
    setTitle('');
    setDescription('');
    setTags([]);
    setErrors({});
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const validateStep = () => {
    const nextErrors = {};
    if (step === 0 && !title.trim()) {
      nextErrors.title = t('addcase.manual_title_required', 'Donnez un titre à votre projet.');
    }
    if (step === 1 && !description.trim()) {
      nextErrors.description = t(
        'addcase.manual_information_required',
        'Ajoutez quelques informations pour expliquer votre projet.'
      );
    }
    if (step === 2) {
      tags.forEach((tag, index) => {
        const name = String(tag.variable || '').trim();
        const value = String(tag.description || '').trim();
        if (!name) nextErrors[`variable-${index}`] = t('settings.variable_required');
        else if (hasUserTagVariableReservedChars(name)) {
          nextErrors[`variable-${index}`] = t('settings.variable_no_space_underscore');
        }
        if (!value) nextErrors[`description-${index}`] = t('settings.description_required');
      });
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const goNext = () => {
    if (validateStep()) setStep((current) => Math.min(current + 1, steps.length - 1));
  };

  const handleCreate = () => {
    if (!validateStep()) return;
    const created = onCreateProject({
      title: title.trim(),
      description: description.trim(),
      customFields: tags.map((tag) => ({
        variable: String(tag.variable || '').trim(),
        description: String(tag.description || '').trim(),
      })),
    });
    if (created) handleClose();
  };

  const updateTag = (index, field, value) => {
    setTags((current) => current.map((tag, tagIndex) => (
      tagIndex === index ? { ...tag, [field]: value } : tag
    )));
    setErrors((current) => {
      const next = { ...current };
      delete next[`${field}-${index}`];
      return next;
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', background: '#fcfcff' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <InfoIcon sx={{ color: primaryColor, fontSize: 28 }} />
          <Box>
            <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 700 }}>
              {t('addcase.manual_creation_button', 'Création manuelle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('addcase.manual_wizard_subtitle', 'Créez votre projet simplement, étape par étape.')}
            </Typography>
          </Box>
        </Box>
        <CloseButton onClick={handleClose} sx={{ color: 'text.secondary' }} />
      </DialogTitle>

      <DialogContent dividers sx={{ px: { xs: 2, sm: 4 }, py: 3 }}>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {step === 0 && (
          <Box sx={{ maxWidth: 650, mx: 'auto' }}>
            <Typography variant="h5" sx={{ color: primaryColor, fontWeight: 700, mb: 1 }}>
              {t('addcase.manual_title_heading', 'Comment souhaitez-vous appeler votre projet ?')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('addcase.manual_title_help', 'Choisissez un nom facile à reconnaître, par exemple « Factures clients 2026 ».')}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label={t('addcase.title', 'Titre du projet')}
              placeholder={t('addcase.manual_title_placeholder', 'Ex. Factures clients 2026')}
              value={title}
              onChange={(event) => { setTitle(event.target.value); setErrors({}); }}
              error={Boolean(errors.title)}
              helperText={errors.title}
              sx={fieldSx}
            />
          </Box>
        )}

        {step === 1 && (
          <Box sx={{ maxWidth: 650, mx: 'auto' }}>
            <Typography variant="h5" sx={{ color: primaryColor, fontWeight: 700, mb: 1 }}>
              {t('addcase.manual_information_heading', 'Parlez-nous de votre projet')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('addcase.manual_information_help', 'Expliquez avec vos mots ce que contiennent vos documents et ce que vous voulez comprendre. Pas besoin de termes techniques.')}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={7}
              label={t('addcase.description', 'Informations sur le projet')}
              placeholder={t('addcase.manual_information_placeholder', 'Ex. Ce projet contient nos contrats. Je veux retrouver les dates, les montants et les noms des clients.')}
              value={description}
              onChange={(event) => { setDescription(event.target.value); setErrors({}); }}
              error={Boolean(errors.description)}
              helperText={errors.description}
              sx={fieldSx}
            />
          </Box>
        )}

        {step === 2 && (
          <Box sx={{ maxWidth: 760, mx: 'auto' }}>
            <Typography variant="h5" sx={{ color: primaryColor, fontWeight: 700, mb: 1 }}>
              {t('addcase.manual_extraction_heading', 'Que voulez-vous extraire ?')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {t('addcase.manual_extraction_help', 'Ajoutez un Smart Tag pour chaque information à retrouver automatiquement. Un Smart Tag est une étiquette intelligente : DataUnfold cherche cette information dans vos documents et la range au même endroit.')}
            </Typography>
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              {t('addcase.manual_extraction_example', 'Exemple : nom_client → Le nom de la personne ou de l’entreprise concernée.')}
            </Alert>

            {tags.map((tag, index) => (
              <Box key={index} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, alignItems: 'flex-start', mb: 2, p: 2, background: '#fff', borderRadius: 2, border: '1px solid #e4e4f2' }}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('addcase.tag_name', 'Nom du Smart Tag')}
                  placeholder={t('addcase.manual_tag_name_placeholder', 'Ex. nom_client')}
                  value={tag.variable}
                  onChange={(event) => updateTag(index, 'variable', event.target.value)}
                  error={Boolean(errors[`variable-${index}`])}
                  helperText={errors[`variable-${index}`]}
                  sx={{ width: { xs: '100%', sm: '34%' }, ...fieldSx }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label={t('addcase.tag_value', 'Valeur à rechercher')}
                  placeholder={t('addcase.manual_tag_value_placeholder', 'Ex. Le nom du client ou de l’entreprise')}
                  value={tag.description}
                  onChange={(event) => updateTag(index, 'description', event.target.value)}
                  error={Boolean(errors[`description-${index}`])}
                  helperText={errors[`description-${index}`]}
                  sx={{ flex: 1, width: { xs: '100%', sm: 'auto' }, ...fieldSx }}
                />
                <CloseButton onClick={() => setTags((current) => current.filter((_, tagIndex) => tagIndex !== index))} ariaLabel={t('common.remove', 'Supprimer')} size="small" sx={{ mt: 0.75 }} />
              </Box>
            ))}

            <Button startIcon={<AddIcon />} onClick={() => setTags((current) => [...current, { variable: '', description: '' }])} variant="outlined" sx={{ mt: 1, color: primaryColor, borderColor: primaryColor, textTransform: 'none', borderRadius: 2 }}>
              {t('addcase.add_custom_tag', 'Ajouter un Smart Tag')}
            </Button>
            {!tags.length && <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{t('addcase.manual_no_tags_hint', 'Vous pouvez passer cette étape si vous ne savez pas encore quoi extraire.')}</Typography>}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
        <Button onClick={() => (step === 0 ? handleClose() : setStep((current) => current - 1))} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          {step === 0 ? t('common.cancel', 'Cancel') : t('common.back', 'Back')}
        </Button>
        {step < steps.length - 1 ? (
          <Button variant="contained" onClick={goNext} sx={{ backgroundColor: primaryColor, textTransform: 'none', borderRadius: 2, px: 3 }}>
            {t('common.next', 'Continue')}
          </Button>
        ) : (
          <Button variant="contained" startIcon={<CreateNewFolderIcon />} onClick={handleCreate} sx={{ backgroundColor: primaryColor, textTransform: 'none', borderRadius: 2, px: 3 }}>
            {t('addcase.create_analysis', 'Créer le projet')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ManualCreationDialog;
